import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { api } from "../lib/api";
import { Image as ImageIcon, CalendarDays, Trophy, PenLine } from "lucide-react";
import PosterStudio from "../components/PosterStudio";
import PosterGallery from "../components/PosterGallery";

const fmtDate = (s) => { if (!s) return ""; try { return new Date(s + (s.length === 10 ? "T12:00:00" : "")).toLocaleDateString("fr-CA", { day: "numeric", month: "long", year: "numeric" }); } catch { return s; } };
const fmtRange = (a, b) => (a && b && a !== b ? `${fmtDate(a)} → ${fmtDate(b)}` : fmtDate(a));

export default function GenerateurAffichesModule() {
  const [events, setEvents] = useState([]);
  const [calendar, setCalendar] = useState([]);
  const [source, setSource] = useState("libre"); // libre | event:<id> | cal:<id>
  const [initial, setInitial] = useState({});
  const [reuseKey, setReuseKey] = useState(0);
  const reuseRef = useRef(false);

  const load = useCallback(async () => {
    const today = new Date().toISOString().slice(0, 10);
    try { const { data } = await api.get("/events"); setEvents((Array.isArray(data) ? data : data.events || []).filter((e) => !e.start_date || e.start_date >= today || (e.end_date && e.end_date >= today))); } catch { /* */ }
    try { const { data } = await api.get(`/accueil-pilotage/calendrier?year=${new Date().getFullYear()}`); setCalendar((data.events || []).filter((e) => !e.start_date || e.start_date >= today)); } catch { /* */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const selected = useMemo(() => {
    if (source.startsWith("event:")) return { kind: "event", e: events.find((x) => x.id === source.slice(6)) };
    if (source.startsWith("cal:")) return { kind: "cal", e: calendar.find((x) => x.id === source.slice(4)) };
    return { kind: "libre" };
  }, [source, events, calendar]);

  useEffect(() => {
    const { kind, e } = selected;
    if (kind === "event" && e) setInitial({ event_title: e.event_title || "", event_dates: fmtRange(e.start_date, e.end_date), event_location: e.venue_name || e.city || "", subtitle: e.short_description || (e.long_description || "").slice(0, 120), teaching: !!e.instructor_teaching, qr_dest: "evenements" });
    else if (kind === "cal" && e) setInitial({ event_title: e.title || "", event_dates: fmtRange(e.start_date, e.end_date), event_location: e.location || "Val-d'Or, Québec", subtitle: e.description || "", teaching: false, qr_dest: "inscription" });
    else if (reuseRef.current) reuseRef.current = false;
    else setInitial({});
  }, [selected]);

  const eventPhotos = selected.kind === "event" && selected.e ? (selected.e.photos || []) : [];
  const reuse = (p) => {
    reuseRef.current = source !== "libre";
    setSource("libre");
    setInitial({ event_title: p.headline || "", ...(p.params || {}) });
    setReuseKey((k) => k + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6" data-testid="generateur-affiches-page">
      <div className="mb-5">
        <div className="text-[#C9A961] text-xs uppercase tracking-widest">Accueil & Pilotage</div>
        <h1 className="text-3xl font-bold text-white flex items-center gap-2"><ImageIcon className="w-7 h-7 text-[#C9A961]" /> Générateur d'affiches IA</h1>
        <p className="text-zinc-400 text-sm mt-1">Choisis un événement à venir (ou pars de zéro), donne ton idée, et l'IA compose l'affiche — avec tes photos ou une image générée. Code QR traçable inclus.</p>
      </div>

      <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4 mb-4" data-testid="affiche-source">
        <div className="text-sm font-semibold text-zinc-200 mb-2">Pour quel événement ?</div>
        <div className="flex flex-wrap gap-2 mb-3">
          <button onClick={() => setSource("libre")} data-testid="affiche-source-libre" className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border ${source === "libre" ? "border-[#C9A961] text-white bg-white/10" : "border-white/15 text-zinc-400 hover:border-white/40"}`}><PenLine className="w-3.5 h-3.5" /> Idée libre</button>
          {events.map((e) => (
            <button key={e.id} onClick={() => setSource(`event:${e.id}`)} data-testid={`affiche-source-event-${e.id}`} className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border ${source === `event:${e.id}` ? "border-[#C9A961] text-white bg-white/10" : "border-white/15 text-zinc-400 hover:border-white/40"}`}>
              <Trophy className="w-3.5 h-3.5 text-amber-400" /> {e.event_title} <span className="text-zinc-500">· {fmtDate(e.start_date)}</span>
            </button>
          ))}
          {calendar.map((e) => (
            <button key={e.id} onClick={() => setSource(`cal:${e.id}`)} data-testid={`affiche-source-cal-${e.id}`} className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border ${source === `cal:${e.id}` ? "border-[#C9A961] text-white bg-white/10" : "border-white/15 text-zinc-400 hover:border-white/40"}`}>
              <CalendarDays className="w-3.5 h-3.5 text-sky-400" /> {e.title} <span className="text-zinc-500">· {fmtDate(e.start_date)}</span>
            </button>
          ))}
        </div>
        {events.length === 0 && calendar.length === 0 && <div className="text-xs text-zinc-500">Aucun événement à venir dans Événements ni dans le Calendrier annuel — pars d'une idée libre.</div>}
      </div>

      <PosterStudio key={`${source}-${reuseKey}`} initial={initial} eventPhotos={eventPhotos} showIdea />
      <PosterGallery onReuse={reuse} />
    </div>
  );
}
